import { Select as AntSelect, SelectProps as AntSelectProps, Form } from 'antd'

/**
 * Class applied to every popup this component renders.
 * Styling lives in `app/frontend/entrypoints/application.css` and caps how wide
 * the popup may grow once it is free to be wider than its trigger.
 */
const SELECT_POPUP_CLASS = 'ava-select-popup'

export interface SelectProps extends Omit<AntSelectProps, 'size'> {
  /**
   * Label text displayed above the select
   */
  label?: string

  /**
   * Error message to display
   */
  error?: string

  /**
   * Helper text displayed below the select
   */
  helperText?: string

  /**
   * Mark the field as required
   */
  required?: boolean

  /**
   * Size of the select
   * @default 'small'
   */
  size?: 'small' | 'medium' | 'large'

  /**
   * Full width select
   */
  block?: boolean

  /**
   * Whether the open popup is forced to the exact width of the closed select box.
   *
   * Defaults to `false` (antd's own default is `true`) so options are not clipped
   * when the select sits in a narrow column — e.g. the Print Station picker, whose
   * labels look like "ZDesigner ZD621-203dpi ZPL (Copy 1) (Print Station 1)".
   * With `false`, rc-select still uses the trigger width as the popup's *minimum*
   * width, so the popup is never narrower than before. Pass `true` to opt out.
   *
   * @default false
   */
  popupMatchSelectWidth?: boolean | number
}

/**
 * Select Component
 *
 * A dropdown select field with label, error, and helper text support.
 *
 * @example
 * <Select
 *   label="Country"
 *   placeholder="Select a country"
 *   options={[
 *     { value: 'us', label: 'United States' },
 *     { value: 'uk', label: 'United Kingdom' },
 *   ]}
 * />
 *
 * @example
 * // Multiple select
 * <Select
 *   label="Tags"
 *   mode="multiple"
 *   placeholder="Select tags"
 *   options={tags}
 * />
 *
 * @example
 * // Searchable select
 * <Select
 *   label="User"
 *   showSearch
 *   filterOption={(input, option) =>
 *     (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
 *   }
 * />
 */
export function Select({
  label,
  error,
  helperText,
  required,
  size = 'small',
  block = true,
  popupMatchSelectWidth = false,
  classNames,
  popupClassName,
  ...props
}: SelectProps) {
  const antSize = size === 'medium' ? 'middle' : size

  // antd resolves the popup class from `classNames.popup.root` *or* the deprecated
  // `popupClassName` — never both — so merge them here instead of overwriting.
  const popupRootClassName = [classNames?.popup?.root, popupClassName, SELECT_POPUP_CLASS]
    .filter(Boolean)
    .join(' ')

  const selectNode = (
    <AntSelect
      size={antSize}
      style={block ? { width: '100%' } : undefined}
      popupMatchSelectWidth={popupMatchSelectWidth}
      classNames={{ ...classNames, popup: { ...classNames?.popup, root: popupRootClassName } }}
      {...props}
    />
  )

  // If there's a label, wrap in Form.Item for consistent styling
  if (label || error || helperText) {
    return (
      <Form.Item
        label={label}
        required={required}
        validateStatus={error ? 'error' : undefined}
        help={error || helperText}
        className="mb-6"
      >
        {selectNode}
      </Form.Item>
    )
  }

  // Standalone select without label
  return selectNode
}
